/**
 * JOB HUNTER › CANDIDATE — /job-hunter/candidates/:profileUid?tab=…
 *
 * One person the agents hunt for: profile + truth file (the only facts any
 * agent may use), the CV library with AI health reviews, the portfolio,
 * WhatsApp alerts with their message templates, and limits.
 */
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { BellRing, Bot, Briefcase, Coins, FileText, Save, ShieldCheck, SlidersHorizontal, Trash2, UserRound } from 'lucide-react';
import { Button } from '@/components/Button';
import { ModelPicker } from '@/components/ModelPicker';
import { JH, JobHunterShell, type CandidateTab } from '@/components/job-hunter/JobHunterShell';
import { Field, Meter, Notice, Pill, Toggle, ToggleRow } from '@/components/job-hunter/ui';
import { CandidatePhoto } from '@/components/job-hunter/candidate/CandidatePhoto';
import { ReadFromCv } from '@/components/job-hunter/candidate/ReadFromCv';
import { timeAgo } from '@/lib/job-hunter/format';
import { TruthFileForm } from '@/components/job-hunter/candidate/TruthFileForm';
import { AlertsPanel } from '@/components/job-hunter/candidate/AlertsPanel';
import { CvLibrary } from '@/components/job-hunter/CvLibrary';
import { PortfolioEditor } from '@/components/job-hunter/PortfolioEditor';
import { refreshJobHunter, setActiveProfile, upsertProfileInStore, useJobHunter } from '@/lib/job-hunter/store';
import { jobHunterService, type JhNotifyOptions, type JhProfile } from '@/services/job-hunter.service';
import type { AiProviderName } from '@/services/settings.service';
import { useAuth } from '@/hooks/useAuth';

const TABS: Array<{ id: CandidateTab; label: string; Icon: typeof UserRound }> = [
  { id: 'profile', label: 'Profile & truth file', Icon: ShieldCheck },
  { id: 'cvs', label: 'CVs', Icon: FileText },
  { id: 'portfolio', label: 'Portfolio', Icon: Briefcase },
  { id: 'alerts', label: 'WhatsApp alerts', Icon: BellRing },
  { id: 'limits', label: 'Limits & AI', Icon: SlidersHorizontal },
];

/** Every field the candidate form can save. */
const EDITABLE: Array<keyof JhProfile> = [
  'name', 'headline', 'active', 'truthFile', 'instantThreshold', 'monthlyAiCallLimit', 'dailyApplyCap', 'decisionTimeoutHours',
  'notifyEnabled', 'notifyPhone', 'notifyAccountUid', 'alertTemplateUid', 'wakeTemplateUid', 'timezone', 'digestHour', 'quietFromHour', 'quietToHour', 'consumeAllFromPhone',
];
const pick = (p: JhProfile) => Object.fromEntries(EDITABLE.map((k) => [k, p[k]])) as Partial<JhProfile>;

function Card({ icon, title, sub, children, aside }: { icon: ReactNode; title: string; sub?: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="jh-card jh-card-pad">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-start gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary-soft text-primary">{icon}</span>
          <div><div className="jh-card-title">{title}</div>{sub && <div className="jh-card-sub max-w-2xl">{sub}</div>}</div>
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

export default function JhCandidatePage() {
  const { profileUid: param } = useParams<{ profileUid: string }>();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : 'profile') as CandidateTab;
  const nav = useNavigate();
  const { user } = useAuth();
  const { profiles, agents } = useJobHunter();
  const [saved, setSaved] = useState<JhProfile | null>(null);
  const [f, setF] = useState<JhProfile | null>(null);
  const [opts, setOpts] = useState<JhNotifyOptions | null>(null);
  const [saving, setSaving] = useState(false);
  const [customModel, setCustomModel] = useState(false);
  const exists = !!param && !!profiles?.some((p) => p.profileUid === param);

  useEffect(() => { if (param && exists) setActiveProfile(param); }, [param, exists]);
  const load = useCallback(async () => {
    if (!param) return;
    try {
      const p = await jobHunterService.profile(param);
      setSaved(p); setF(p); setCustomModel(!!p.aiProvider);
    } catch { /* toasted */ }
  }, [param]);
  useEffect(() => { setSaved(null); setF(null); void load(); }, [load]);
  useEffect(() => { jobHunterService.notifyOptions().then(setOpts).catch(() => undefined); }, []);

  const dirty = useMemo(() => !!f && !!saved && (JSON.stringify(pick(f)) !== JSON.stringify(pick(saved)) || customModel !== !!saved.aiProvider || (customModel && (f.aiProvider !== saved.aiProvider || f.aiModel !== saved.aiModel))), [f, saved, customModel]);
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  if (profiles && param && !exists) return <Navigate to={JH.CANDIDATES} replace />;

  const set = <K extends keyof JhProfile>(k: K, v: JhProfile[K]) => setF((x) => (x ? { ...x, [k]: v } : x));
  /** Changes the server already saved (templates linked, portfolio) — applied to both copies so they don't count as unsaved. */
  const syncSaved = (patch: Partial<JhProfile>) => { setSaved((x) => (x ? { ...x, ...patch } : x)); setF((x) => (x ? { ...x, ...patch } : x)); };

  const save = async () => {
    if (!f) return;
    if (!f.name.trim()) { toast.error('The candidate needs a name'); setParams({}); return; }
    setSaving(true);
    try {
      await jobHunterService.updateProfile(f.profileUid, {
        ...pick(f), headline: f.headline ?? '', notifyPhone: f.notifyPhone ?? '', notifyAccountUid: f.notifyAccountUid ?? '',
        alertTemplateUid: f.alertTemplateUid ?? '', wakeTemplateUid: f.wakeTemplateUid ?? '',
        aiProvider: customModel ? f.aiProvider ?? '' : '', aiModel: customModel ? f.aiModel ?? '' : '',
      });
      const fresh = await jobHunterService.profile(f.profileUid);
      setSaved(fresh); setF(fresh); upsertProfileInStore(fresh);
      toast.success('Candidate saved');
    } catch { /* toasted */ } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!f || !window.confirm(`Delete “${f.name}”? Their agents stop; matches stay in the database.`)) return;
    try { await jobHunterService.deleteProfile(f.profileUid); toast.success('Candidate deleted'); await refreshJobHunter(); nav(JH.CANDIDATES); } catch { /* toasted */ }
  };

  /** The server read a CV into the profile: take its name / headline / truth file, keep any other unsaved edits. */
  const onCvSynced = async () => {
    if (!param) return;
    try {
      const fresh = await jobHunterService.profile(param);
      setSaved(fresh);
      setF((x) => (x ? { ...x, name: fresh.name, headline: fresh.headline, truthFile: fresh.truthFile, lastCvSync: fresh.lastCvSync } : fresh));
      upsertProfileInStore(fresh);
    } catch { /* toasted */ }
  };
  const dismissSync = async () => {
    if (!f) return;
    syncSaved({ lastCvSync: null });
    try { await jobHunterService.dismissCvSync(f.profileUid); } catch { /* toasted */ }
  };

  const onLinked = async () => {
    if (!f) return;
    try { const p = await jobHunterService.profile(f.profileUid); syncSaved({ alertTemplateUid: p.alertTemplateUid, wakeTemplateUid: p.wakeTemplateUid, notifyAccountUid: p.notifyAccountUid }); } catch { /* toasted */ }
  };

  const counts = profiles?.find((p) => p.profileUid === param)?.counts;
  const budget = saved?.budget;

  return (
    <JobHunterShell section="Candidates" description="Profile, CVs, portfolio, alerts and limits">
      {!f || !saved ? <div className="h-96 animate-pulse rounded-2xl bg-surface-alt" /> : (
        <>
          <div className="jh-hero mb-4 !p-4 sm:!p-5">
            <div className="flex flex-wrap items-center gap-4">
              <CandidatePhoto profile={saved} size={64} onChange={(photo) => { syncSaved({ photo }); upsertProfileInStore({ ...saved, photo }); }} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="truncate !text-[20px]">{saved.name}</h1>
                  {!saved.active && <Pill tone="neutral">Paused</Pill>}
                </div>
                <p className="mt-0.5 text-[13px] text-text-secondary">{saved.headline || 'Add a headline in the profile'}</p>
              </div>
              <div className="flex flex-wrap gap-2 text-[12px]">
                <button type="button" onClick={() => setParams({ tab: 'cvs' })} className="jh-chip !py-1.5 hover:!bg-primary-soft hover:!text-primary"><FileText size={12} />{counts?.cvs ?? 0} CV{counts?.cvs === 1 ? '' : 's'}</button>
                <Link to={JH.AGENTS} className="jh-chip !py-1.5 hover:!bg-primary-soft hover:!text-primary"><Bot size={12} />{agents?.filter((a) => a.active).length ?? counts?.activeAgents ?? 0}/{agents?.length ?? counts?.agents ?? 0} agents active</Link>
              </div>
            </div>
          </div>

          <div className="jh-seg mb-5">
            {TABS.map(({ id, label, Icon }) => (
              <button key={id} type="button" aria-pressed={tab === id} onClick={() => setParams(id === 'profile' ? {} : { tab: id })}><Icon size={13} />{label}</button>
            ))}
          </div>

          {tab === 'profile' && (
            <div className="space-y-4">
              {f.lastCvSync && (
                <Notice tone={f.lastCvSync.error ? 'warning' : 'success'} title={`Filled from “${f.lastCvSync.label}” ${timeAgo(f.lastCvSync.at)}`}
                  action={<Button size="sm" variant="ghost" onClick={() => void dismissSync()}>Got it</Button>}>
                  {f.lastCvSync.changed.length ? <>Updated: <b>{f.lastCvSync.changed.join(', ')}</b>. Check these fields and correct anything the CV got wrong, then save.</> : 'The CV had nothing new for the profile.'}
                  {f.lastCvSync.error && <span className="mt-1 block text-warning">{f.lastCvSync.error}</span>}
                </Notice>
              )}
              <Card icon={<UserRound size={17} />} title="Candidate" sub="Name and status. A paused candidate's agents don't screen, score or alert."
                aside={<div className="flex items-center gap-2"><span className="text-xs font-semibold text-text-secondary">{f.active ? 'Hunting' : 'Paused'}</span><Toggle checked={f.active} onChange={(v) => set('active', v)} label="Active" /></div>}>
                <div className="@container"><div className="grid gap-4 @xl:grid-cols-2">
                  <Field label="Name"><input className="jh-input" value={f.name} onChange={(e) => set('name', e.target.value)} maxLength={120} /></Field>
                  <Field label="Headline"><input className="jh-input" value={f.headline ?? ''} onChange={(e) => set('headline', e.target.value)} placeholder="Senior Flutter & backend engineer" maxLength={200} /></Field>
                </div></div>
              </Card>
              <div>
                <div className="mb-2 flex items-center gap-2.5 px-1">
                  <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary-soft text-primary"><ShieldCheck size={16} /></span>
                  <h2 className="text-[15px] font-semibold tracking-tight">Truth file</h2>
                </div>
                <TruthFileForm value={f.truthFile} onChange={(tf) => set('truthFile', tf)}
                  actions={<ReadFromCv profileUid={f.profileUid} dirty={dirty} onSynced={() => void onCvSynced()} />} />
              </div>
              {user?.role === 'company_admin' && <div className="flex justify-end"><Button variant="ghost" iconLeft={<Trash2 size={14} />} onClick={remove}>Delete this candidate</Button></div>}
            </div>
          )}

          {tab === 'cvs' && <CvLibrary profile={saved} onProfileSynced={() => void onCvSynced()} />}

          {tab === 'portfolio' && (
            <Card icon={<Briefcase size={17} />} title="Portfolio" sub="Apps and sites the candidate built. Paste a Google Play, App Store, website or GitHub link — the public details are read automatically, and every agent cites them in matches and cover notes.">
              <PortfolioEditor profileUid={f.profileUid} items={f.portfolio ?? []} onChange={(portfolio) => syncSaved({ portfolio })} />
            </Card>
          )}

          {tab === 'alerts' && <AlertsPanel f={f} saved={saved} set={set} opts={opts} dirty={dirty} onLinked={() => void onLinked()} />}

          {tab === 'limits' && (
            <Card icon={<SlidersHorizontal size={17} />} title="Limits & AI" sub="Shared by all of this candidate's agents. Each agent also has its own daily scoring limit (Agents).">
              <div className="@container"><div className="grid gap-5 @4xl:grid-cols-2">
                <div className="grid content-start gap-3 @md:grid-cols-2">
                  <Field label="AI calls per month" hint="Scoring, drafts and CV reviews together."><input className="jh-input" type="number" min={0} value={f.monthlyAiCallLimit} onChange={(e) => set('monthlyAiCallLimit', Number(e.target.value) || 0)} /></Field>
                  <Field label="Applications per day"><input className="jh-input" type="number" min={1} value={f.dailyApplyCap} onChange={(e) => set('dailyApplyCap', Number(e.target.value) || 1)} /></Field>
                  <Field label="Skip undecided after (h)" hint="0 keeps them forever"><input className="jh-input" type="number" min={0} value={f.decisionTimeoutHours} onChange={(e) => set('decisionTimeoutHours', Number(e.target.value) || 0)} /></Field>
                  {budget && (
                    <div className="rounded-xl border border-border p-3 text-[12.5px] @md:col-span-2">
                      <div className="flex items-center justify-between gap-2 font-semibold"><span className="inline-flex items-center gap-2"><Coins size={14} className="text-primary" />This month</span><span className="tabular-nums">{budget.monthCalls} / {saved.monthlyAiCallLimit}</span></div>
                      <div className="mt-2"><Meter value={budget.monthCalls} max={saved.monthlyAiCallLimit} /></div>
                      <div className="mt-1.5 text-text-muted">{budget.monthLeft} AI calls left · {(saved.usage?.credits ?? 0).toFixed(2)} credits used</div>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <ToggleRow title="Use a specific AI model" hint="For scoring and drafts. Off = the workspace's default model (Settings → AI). The CV review lets you pick a model per run." checked={customModel} onChange={setCustomModel} />
                  {customModel && <ModelPicker value={f.aiProvider ? { provider: f.aiProvider as AiProviderName, model: f.aiModel ?? '' } : undefined} onChange={(v) => setF((x) => (x ? { ...x, aiProvider: v.provider, aiModel: v.model } : x))} />}
                </div>
              </div></div>
            </Card>
          )}

          {dirty && (
            <div className="jh-savebar">
              <div className="flex items-center gap-2 text-[13px] font-semibold"><Pill tone="warning">Unsaved changes</Pill></div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => { setF(saved); setCustomModel(!!saved.aiProvider); }}>Discard</Button>
                <Button loading={saving} iconLeft={<Save size={14} />} onClick={save}>Save candidate</Button>
              </div>
            </div>
          )}
        </>
      )}
    </JobHunterShell>
  );
}
